import { useLayoutEffect } from 'react'
import { styled } from 'styled-components'

import {
	GroupeDeQuestionsPublicodes,
	QuestionPublicodes,
} from '@/domaine/questions'
import { Situation } from '@/domaine/Situation'
import { MergedSimulatorMetadata } from '@/hooks/useSimulatorsMetadata'
import { useTracking } from '@/hooks/useTracking'

import { type ConseillersEntreprisesVariant } from '../ConseillersEntreprises/BoutonConseillersEntreprises'
import { ACCUEIL, SIMULATION_COMMENCEE } from '../PianoAnalytics'
import SimulateurWarning from '../SimulateurWarning'
import { Actions } from './Actions'
import { AutoScrollToQuestionsProvider } from './AutoScrollToQuestions'
import { ÉLÉMENT_DÉTAILS_ID } from './Boutons/BoutonDétail'
import {
	ComposantQuestionFournie,
	GroupeDeQuestionsFournies,
} from './Questions/ComposantQuestionFournie'
import { ZoneDeSaisie } from './ZoneDeSaisie'

type Props<S extends Situation> = {
	metadata: MergedSimulatorMetadata
	montantsÀSaisir: React.ReactNode
	questionsPublicodesPrincipales?: QuestionPublicodes[]
	groupesDeQuestionsPublicodes?: Record<string, GroupeDeQuestionsPublicodes>
	questionsFourniesPrincipales?: ComposantQuestionFournie<S>[]
	groupesDeQuestionsFournies?: Record<string, GroupeDeQuestionsFournies<S>>
	situation?: S
	questionsPrincipalesRépondues?: boolean
	onReset: () => void
	avertissement?: React.ReactNode
	conseillersEntreprisesVariant?: ConseillersEntreprisesVariant
	simulationEstCommencée: boolean
	détail?: React.ReactNode
}

export const Simulateur = <S extends Situation = Situation>({
	metadata,
	montantsÀSaisir,
	questionsPublicodesPrincipales,
	groupesDeQuestionsPublicodes,
	questionsFourniesPrincipales,
	groupesDeQuestionsFournies,
	situation,
	questionsPrincipalesRépondues,
	onReset,
	avertissement,
	conseillersEntreprisesVariant,
	simulationEstCommencée,
	détail,
}: Props<S>) => {
	const { trackPage } = useTracking()

	useLayoutEffect(() => {
		trackPage({ name: simulationEstCommencée ? SIMULATION_COMMENCEE : ACCUEIL })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [simulationEstCommencée])

	return (
		<Container>
			<SimulateurWarning
				metadata={metadata}
				informationsComplémentaires={avertissement}
			/>

			{/* Année précédente */}

			<AutoScrollToQuestionsProvider>
				<ZoneDeSaisie
					questionsPublicodesPrincipales={questionsPublicodesPrincipales}
					groupesDeQuestionsPublicodes={groupesDeQuestionsPublicodes}
					questionsFourniesPrincipales={questionsFourniesPrincipales}
					groupesDeQuestionsFournies={groupesDeQuestionsFournies}
					situation={situation}
					questionsPrincipalesRépondues={questionsPrincipalesRépondues}
					onReset={onReset}
					montants={montantsÀSaisir}
				/>
			</AutoScrollToQuestionsProvider>

			<Actions
				conseillersEntreprisesVariant={conseillersEntreprisesVariant}
				avecBoutonDétails={!!détail}
				avecBoutonPartage={simulationEstCommencée}
			/>

			{détail && (
				<div id={ÉLÉMENT_DÉTAILS_ID} tabIndex={-1}>
					{détail}
				</div>
			)}
		</Container>
	)
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spacings.lg};
`
